// Content Section Variants Configuration
// About, Key Numbers, Accreditation, Academic Deliverables, Scholar
//
// MANDATORY when adding a variant here: give it a human-readable `name`. It is
// spread into SECTION_VARIANTS by ./index.ts and read through
// `getVariantDetails()` by the page builder and by **Widget Management**
// (`/admin/widget-management`), where that string is the only label identifying
// the widget before someone deletes it from live pages. Miss it and the screen
// falls back to the raw id.
//
// Full checklist for a new section or variant: top of ../sectionTypes.ts.

import { createAbout, updateAbout } from "@/api/admin/about";
import { createAccordion, updateAccordion } from "@/api/admin/accordion";
import {
  createLiveLectureConfig,
  updateLiveLectureConfig,
} from "@/api/admin/liveLectureConfig";
import {
  createAccreditation,
  updateAccreditation,
} from "@/api/admin/accreditation";
import { createCounseling, updateCounseling } from "@/api/admin/counseling";
import {
  createCourseSpecialisations,
  updateCourseSpecialisations,
} from "@/api/admin/courseSpecialisations";
import { createDeliverable, updateDeliverable } from "@/api/admin/deliverable";
import { createKeyNumbers, updateKeyNumbers } from "@/api/admin/keyNumbers";
import {
  createDigitalLearning,
  updateDigitalLearning,
} from "@/api/admin/digitalLearning";
import {
  createProgramProspects,
  updateProgramProspects,
} from "@/api/admin/programProspects";
import { createScholar, updateScholar } from "@/api/admin/scholar";
import { createSkillCards, updateSkillCards } from "@/api/admin/skillCards";
import { createLatestBlog, updateLatestBlog } from "@/api/admin/latestBlog";
import {
  createPopularBlog,
  updatePopularBlog,
} from "@/api/admin/popularBlog";
import { createBlogList, updateBlogList } from "@/api/admin/blogList";
import {
  createAuthorProfile,
  updateAuthorProfile,
} from "@/api/admin/authorProfile";
import { createAuthorCard, updateAuthorCard } from "@/api/admin/authorCard";
import {
  createAuthorByline,
  updateAuthorByline,
} from "@/api/admin/authorByline";
import { getActiveAuthorsForWidget } from "@/api/admin/author";
import {
  createCounselorCallCta,
  updateCounselorCallCta,
} from "@/api/admin/counselorCallCta";
import {
  createBreadcrumbBanner,
  updateBreadcrumbBanner,
} from "@/api/admin/breadcrumbBanner";
import { Form_1 } from "@/components/organisms/admin/forms";
import {
  activateMetadata,
  getPreviewData,
  isCmsShowFlagOn,
} from "@/utils/variantHelpers";
import { extractMetadataFields } from "@/helpers";
import type { PageState } from "@/store/slices/page";
import {
  setKeyNumbers1026,
  setKeyNumbers1036,
  setKeyNumbers1039,
  setAbout1028,
  setAbout1037,
  setAbout5005,
  setAbout1054,
  setAbout1057,
  setKeyNumbers1055,
  setAccreditation1052,
  setAcademicDeliverables1031,
  setAbout1,
  setAbout2,
  setAbout3,
  setAbout4,
  setAbout5,
  setAcademicDeliverables1,
  setAcademicDeliverables2,
  setAcademicDeliverables1001,
  setAcademicDeliverables1002,
  setAcademicDeliverables1006,
  setAccordion1,
  setAccreditation1,
  setAccreditation2,
  setAccreditation3,
  setAccreditation4,
  setAccreditation5,
  setAccreditation1000,
  setCounseling1,
  setCourseSpecialisations1,
  setKeyNumbers1,
  setKeyNumbers2,
  setDigitalLearning1,
  setProgramProspects1,
  setScholar1,
  setSkillCards3000,
  setLatestBlogs1,
  setLatestBlogs2,
  setPopularBlogs1,
  setBlogList1,
  setAuthorProfile3000,
  setAuthorCard3000,
  setAuthorByline3000,
  setCounselorCallCta1,
  setBreadcrumbBanner1,
  setLiveLecture1,
  setLiveLecture1011,
  setLiveLecture1050,
  setKeyNumbers5006,
  setKeyNumbers5007,
} from "@/store/slices/page";
import About1 from "@/widgets/about/About1";
import About2 from "@/widgets/about/About2";
import InstituteSection from "@/widgets/about/InstituteSection";
import AboutMegaFooter from "@/widgets/about/AboutMegaFooter";
import About3 from "@/widgets/about/About3";
import AcademicDeliverables from "@/widgets/academicdeliverables/AcademicDeliverables1";
import AcademicDeliverables2 from "@/widgets/academicdeliverables/AcademicDeliverables2";
import AcademicDeliverables1001 from "@/widgets/academicdeliverables/AcademicDeliverables1001";
import AcademicDeliverables1002 from "@/widgets/academicdeliverables/AcademicDeliverables1002";
import AcademicDeliverables1006 from "@/widgets/academicdeliverables/AcademicDeliverables1006";
import Accreditation from "@/widgets/accreditation/Accreditation1";
import Accreditation2 from "@/widgets/accreditation/Accreditation2";
import Accreditation3 from "@/widgets/accreditation/Accreditation3";
import Accreditation4 from "@/widgets/accreditation/Accreditation4";
import Accreditation5 from "@/widgets/accreditation/Accreditation5";
import Accreditation1000 from "@/widgets/accreditation/Accreditation1000";
import GetFreeCounselling1 from "@/widgets/getfreecounselling/GetFreeCounselling1";
import SkillCards1 from "@/widgets/skillcards/SkillCards1";
import Stats from "@/widgets/keynumbers/KeyNumbers1";
import KeyNumbers2 from "@/widgets/keynumbers/KeyNumbers2";
import DigitalLearning1 from "@/widgets/digitalLearning/DigitalLearning1";
import Scholar from "@/widgets/scholar/Scholar1";
import LatestBlogs1 from "@/widgets/latestblogs/LatestBlogs1";
import LatestBlogs2 from "@/widgets/latestblogs/LatestBlogs2";
import PopularBlogs1 from "@/widgets/popularblogs/PopularBlogs1";
import BlogList1 from "@/widgets/bloglist/BlogList1";
import AuthorProfile1 from "@/widgets/authorprofile/AuthorProfile1";
import AuthorCard1 from "@/widgets/authorcard/AuthorCard1";
import AuthorByline1 from "@/widgets/authorbyline/AuthorByline1";
import CounselorCallCta1 from "@/widgets/counselorcallcta/CounselorCallCta1";
import BreadcrumbBanner1 from "@/widgets/breadcrumbbanner/BreadcrumbBanner1";
import ProgramProspects1 from "@/widgets/programprospects/ProgramProspects1";
import CourseSpecialisation1 from "@/widgets/coursespecialisations/CourseSpecialisation1";
import LiveLecture1 from "@/widgets/liveLecture/liveLecture1";
import LiveLecture1011 from "@/widgets/liveLecture/liveLecture1011";
import LiveLecture1050 from "@/widgets/liveLecture/liveLecture1050";
import ExaminationGuidelines1 from "@/widgets/examinationguidelines/ExaminationGuidelines1";
import { ABOUT_1_CONSTANTS } from "../fields/about/about_1";
import { ABOUT_2_CONSTANTS } from "../fields/about/about_2";
import { ABOUT_3_CONSTANTS } from "../fields/about/about_3";
import {
  ABOUT_4_CONSTANTS,
  ABOUT_4_SEED_METADATA,
} from "../fields/about/about_4";
import { ABOUT_5_CONSTANTS } from "../fields/about/about_5";
import { ACADEMIC_DELIVERABLES_1_CONSTANTS } from "../fields/academicDeliverables/academicDeliverables_1";
import { ACADEMIC_DELIVERABLES_2_CONSTANTS } from "../fields/academicDeliverables/academicDeliverables_2";
import { ACADEMIC_DELIVERABLES_1001_CONSTANTS } from "../fields/academicDeliverables/academicDeliverables_1001";
import { ACADEMIC_DELIVERABLES_1002_CONSTANTS } from "../fields/academicDeliverables/academicDeliverables_1002";
import { ACADEMIC_DELIVERABLES_1006_CONSTANTS } from "../fields/academicDeliverables/academicDeliverables_1006";
import { ACCORDION_1_CONSTANTS } from "../fields/accordion/accordion_1";
import { ACCREDITATION_1_CONSTANTS } from "../fields/accreditation/accreditation_1";
import { ACCREDITATION_2_CONSTANTS } from "../fields/accreditation/accreditation_2";
import { ACCREDITATION_3_CONSTANTS } from "../fields/accreditation/accreditation_3";
import { ACCREDITATION_4_CONSTANTS } from "../fields/accreditation/accreditation_4";
import { ACCREDITATION_5_CONSTANTS } from "../fields/accreditation/accreditation_5";
import { ACCREDITATION_1000_CONSTANTS } from "../fields/accreditation/accreditation_1000";
import { ACCREDITATION_1052_CONSTANTS } from "../fields/accreditation/accreditation_1052";
import Accreditation1052 from "@/widgets/accreditation/Accreditation1052";
import { COUNSELING_1_CONSTANTS } from "../fields/counseling/counseling_1";
import { COURSE_SPECIALISATIONS_1_CONSTANTS } from "../fields/courseSpecialisations/courseSpecialisations_1";
import { KEY_NUMBERS_1_CONSTANTS } from "../fields/keyNumbers/keyNumbers_1";
import { KEY_NUMBERS_2_CONSTANTS } from "../fields/keyNumbers/keyNumbers_2";
import { DIGITAL_LEARNING_1_CONSTANTS } from "../fields/digitalLearning/digitalLearning_1";
import { PROGRAM_PROSPECTS_1_CONSTANTS } from "../fields/programProspects/programProspects_1";
import { SCHOLAR_1_CONSTANTS } from "../fields/scholar/scholar_1";
import { LATEST_BLOGS_1_CONSTANTS } from "../fields/latestBlogs/latest_blogs_1";
import { LATEST_BLOGS_2_CONSTANTS } from "../fields/latestBlogs/latest_blogs_2";
import { POPULAR_BLOGS_1_CONSTANTS } from "../fields/popularBlogs/popular_blogs_1";
import { BLOG_LIST_1_CONSTANTS } from "../fields/blogList/blog_list_1";
import { AUTHOR_PROFILE_1_CONSTANTS } from "../fields/authorProfile/author_profile_1";
import { AUTHOR_CARD_1_CONSTANTS } from "../fields/authorCard/author_card_1";
import { AUTHOR_BYLINE_1_CONSTANTS } from "../fields/authorByline/author_byline_1";
import { COUNSELOR_CALL_CTA_1_CONSTANTS } from "../fields/counselorCallCta/counselor_call_cta_1";
import { BREADCRUMB_BANNER_1_CONSTANTS } from "../fields/breadcrumbBanner/breadcrumb_banner_1";
import { LIVE_LECTURE_1_CONSTANTS } from "../fields/liveLecture/liveLecture_1";
import { LIVE_LECTURE_1011_CONSTANTS } from "../fields/liveLecture/liveLecture_1011";
import { LIVE_LECTURE_1050_CONSTANTS } from "../fields/liveLecture/liveLecture_1050";
import {
  ABOUT_VARIANTS,
  ACADEMIC_DELIVERABLES_VARIANTS,
  ACCORDION_VARIANTS,
  ACCREDITATION_VARIANTS,
  COUNSELING_VARIANTS,
  COURSE_SPECIALISATIONS_VARIANTS,
  KEY_NUMBERS_VARIANTS,
  DIGITAL_LEARNING_VARIANTS,
  PROGRAM_PROSPECTS_VARIANTS,
  SCHOLAR_VARIANTS,
  SKILL_CARDS_VARIANTS,
  LATEST_BLOGS_VARIANTS,
  POPULAR_BLOGS_VARIANTS,
  BLOG_LIST_VARIANTS,
  AUTHOR_PROFILE_VARIANTS,
  AUTHOR_CARD_VARIANTS,
  AUTHOR_BYLINE_VARIANTS,
  COUNSELOR_CALL_CTA_VARIANTS,
  BREADCRUMB_BANNER_VARIANTS,
  LIVE_LECTURE_VARIANTS,
  SECTION_TYPES,
} from "../sectionTypes";
import { KEY_NUMBERS_1026_CONSTANTS } from "../fields/keyNumbers/keyNumbers_1026";
import { SKILL_CARDS_3000_CONSTANTS } from "../fields/skillCards/skillCards_3000";
import KeyNumbers1026 from "@/widgets/keynumbers/KeyNumbers1026";
import { ABOUT_1028_CONSTANTS } from "../fields/about/about_1028";
import About1028 from "@/widgets/about/About1028";
import { ACADEMIC_DELIVERABLES_1031_CONSTANTS } from "../fields/academicDeliverables/academicDeliverables_1031";
import AcademicDeliverables1031 from "@/widgets/academicdeliverables/AcademicDeliverables1031";
import { KEY_NUMBERS_1036_CONSTANTS } from "../fields/keyNumbers/keyNumbers_1036";
import KeyNumbers1036 from "@/widgets/keynumbers/KeyNumbers1036";
import { KEY_NUMBERS_1039_CONSTANTS } from "../fields/keyNumbers/keyNumbers_1039";
import KeyNumbers1039 from "@/widgets/keynumbers/KeyNumbers1039";
import { ABOUT_1037_CONSTANTS } from "../fields/about/about_1037";
import { ABOUT_5005_CONSTANTS } from "../fields/about/about_5005";
import { ABOUT_1054_CONSTANTS } from "../fields/about/about_1054";
import About1054 from "@/widgets/about/About1054";
import { ABOUT_1057_CONSTANTS } from "../fields/about/about_1057";
import About1057 from "@/widgets/about/About1057";
import { KEY_NUMBERS_5006_CONSTANTS } from "../fields/keyNumbers/keyNumbers_5006";
import { KEY_NUMBERS_5007_CONSTANTS } from "../fields/keyNumbers/keyNumbers_5007";
import { KEY_NUMBERS_1055_CONSTANTS } from "../fields/keyNumbers/keyNumbers_1055";
import KeyNumbers1055 from "@/widgets/keynumbers/KeyNumbers1055";
import About1037 from "@/widgets/about/About1037";
import About5005 from "@/widgets/about/About5005";
import KeyNumbers5006 from "@/widgets/keynumbers/KeyNumbers5006";
import KeyNumbers5007 from "@/widgets/keynumbers/KeyNumbers5007";

export const CONTENT_SECTION_VARIANTS = {
  [SECTION_TYPES.ABOUT]: [
    {
      id: ABOUT_VARIANTS.ABOUT_1,
      name: "About 1",
      description: "About section with images and metadata",
      componentFields: ABOUT_1_CONSTANTS.ABOUT_FIELDS_CONFIG,
      ...extractMetadataFields(ABOUT_1_CONSTANTS),
      showTable: true,
      setAction: setAbout1,
      formComponent: Form_1,
      previewComponent: About1,
      selector: (state: PageState) => state.about_1,
      createMethod: createAbout,
      updateMethod: updateAbout,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: ABOUT_VARIANTS.ABOUT_2,
      name: "About 2",
      description: "About section with key highlights and lead form",
      componentFields: ABOUT_2_CONSTANTS.ABOUT_FIELDS_CONFIG,
      ...extractMetadataFields(ABOUT_2_CONSTANTS),
      showTable: true,
      setAction: setAbout2,
      formComponent: Form_1,
      previewComponent: About2,
      selector: (state: PageState) => state.about_2,
      createMethod: createAbout,
      updateMethod: updateAbout,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              title_1: data.title_1 || "Program",
              title_1_color: data.title_1_color || "rgba(255, 255, 255, 0.8)",
              title_2: data.title_2 || "Overview",
              title_2_gradient_start_color: data.title_2_gradient_start_color || "#FFFFFF",
              title_2_gradient_end_color: data.title_2_gradient_end_color || "#02A7B6",
              title_2_gradient_angle: data.title_2_gradient_angle || "90deg",
              description: data.description || "",
              description_text_color: data.description_text_color || "rgba(255, 255, 255, 0.9)",
              backvariant: data.backvariant || "variant_1",
              gradient_color_1: data.gradient_color_1 || "#00D4EB",
              gradient_color_2: data.gradient_color_2 || "#000000",
              gradient_color_3: data.gradient_color_3 || "#000000",
              gradient_color_4: data.gradient_color_4 || "#5345C3",
              background_color: data.background_color || "#000000",
              enable_lead_form: data.enable_lead_form || false,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: ABOUT_VARIANTS.ABOUT_5,
      name: "About — InstituteSection",
      description:
        "About — InstituteSection",
      componentFields: ABOUT_5_CONSTANTS.ABOUT_FIELDS_CONFIG,
      showTable: false,
      setAction: setAbout5,
      formComponent: Form_1,
      previewComponent: InstituteSection,
      selector: (state: PageState) => state.about_5,
      createMethod: createAbout,
      updateMethod: updateAbout,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              title: data.title ?? "About",
              title_text_color: data.title_text_color || "#000000",
              title_1: data.title_1 ?? "IIT Kharagpur",
              title_1_color: data.title_1_color ?? "",
              description: data.description ?? "",
              description_text_color: data.description_text_color ?? "",
              desktop_image: data.desktop_image ?? "",
              mobile_image: data.mobile_image ?? "",
              alt_image: data.alt_image ?? "About",
              read_more_text: data.read_more_text ?? "Know More",
              read_more_color: data.read_more_color ?? "#FFFFFF",
              read_more_background_color:
                data.read_more_background_color ?? "",
              background_color: data.background_color || "#FFFFFF",
              cta_link: data.cta_link ?? "",
              image_on_right: data.image_on_right,
              status: data.status !== false,
            },
          },
        };
      },
    },
    {
      id: ABOUT_VARIANTS.ABOUT_4,
      name: "About 4 — Mega footer",
      description:
        "About 4 — Mega footer - Gradient footer with three columns; dynamic rows for headings, links, rich text, social icons, contact lines",
      componentFields: ABOUT_4_CONSTANTS.ABOUT_FIELDS_CONFIG,
      ...extractMetadataFields(ABOUT_4_CONSTANTS),
      showTable: true,
      setAction: setAbout4,
      formComponent: Form_1,
      previewComponent: AboutMegaFooter,
      selector: (state: PageState) => state.about_4,
      createMethod: createAbout,
      updateMethod: updateAbout,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const meta =
          data.metadata?.length > 0 ?
            activateMetadata(data.metadata)
          : ABOUT_4_SEED_METADATA;
        return {
          data: {
            data: {
              ...data,
              metadata: meta,
              footer_gradient_start_color:
                data.footer_gradient_start_color ?? "#2E1C81",
              footer_gradient_end_color:
                data.footer_gradient_end_color ?? "#15094C",
              footer_gradient_angle: data.footer_gradient_angle ?? "147.3",
              footer_gradient_stop_1: data.footer_gradient_stop_1 ?? "18",
              footer_gradient_stop_2: data.footer_gradient_stop_2 ?? "58",
              footer_accent_border_color:
                data.footer_accent_border_color ?? "#346491",
              footer_text_color: data.footer_text_color ?? "#FFFFFF",
              footer_copyright_html:
                data.footer_copyright_html ??
                "<p>© Copyright 2026 of IIT Kharagpur | All Rights Reserved | <a href=\"/privacy\">Privacy Policy</a></p>",
              status: data.status !== false,
            },
          },
        };
      },
    },
    {
      id: ABOUT_VARIANTS.ABOUT_3,
      name: "About 3",
      description: "Two-column About block with key highlights, highlight callout & lead-drawer CTA",
      componentFields: ABOUT_3_CONSTANTS.ABOUT_FIELDS_CONFIG,
      ...extractMetadataFields(ABOUT_3_CONSTANTS),
      showTable: true,
      setAction: setAbout3,
      formComponent: Form_1,
      previewComponent: About3,
      selector: (state: PageState) => state.about_3,
      createMethod: createAbout,
      updateMethod: updateAbout,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              metadata_1: activateMetadata(data.metadata_1),
              status: true,
            },
          },
        };
      },
    },
    {
      id: ABOUT_VARIANTS.ABOUT_1037,
      name: "About 1037 — BDU About the programme",
      description:
        "BDU programme overview — heading, gradient rule, justified copy, keypoint chips and the site lead form",
      componentFields: ABOUT_1037_CONSTANTS.ABOUT_FIELDS_CONFIG,
      ...extractMetadataFields(ABOUT_1037_CONSTANTS),
      showTable: true,
      setAction: setAbout1037,
      formComponent: Form_1,
      previewComponent: About1037,
      selector: (state: PageState) => state.about_1037,
      createMethod: createAbout,
      updateMethod: updateAbout,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: ABOUT_VARIANTS.ABOUT_5005,
      name: "About 5005 — KUK About the programme",
      description:
        "KUK programme overview — heading with a { } highlight, intro copy, and key-content cards that scroll horizontally from 768px up and collapse into an accordion below it",
      componentFields: ABOUT_5005_CONSTANTS.ABOUT_FIELDS_CONFIG,
      ...extractMetadataFields(ABOUT_5005_CONSTANTS),
      showTable: true,
      setAction: setAbout5005,
      formComponent: Form_1,
      previewComponent: About5005,
      selector: (state: PageState) => state.about_5005,
      createMethod: createAbout,
      updateMethod: updateAbout,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget drops status-false rows itself,
              // so the preview must see the same cards the live page does.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: ABOUT_VARIANTS.ABOUT_1054,
      name: "About 1054 — KUK Home About Us",
      description:
        "KUK home About Us — a click-to-play YouTube facade beside a ruled heading, justified copy and a Know More outline button",
      componentFields: ABOUT_1054_CONSTANTS.ABOUT_FIELDS_CONFIG,
      ...extractMetadataFields(ABOUT_1054_CONSTANTS),
      showTable: true,
      setAction: setAbout1054,
      formComponent: Form_1,
      previewComponent: About1054,
      selector: (state: PageState) => state.about_1054,
      createMethod: createAbout,
      updateMethod: updateAbout,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget drops status-false rows itself,
              // so the preview must see the same rows the live page does.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: ABOUT_VARIANTS.ABOUT_1057,
      name: "About 1057 — KUK Vice Chancellor's message",
      description:
        "KUK home Vice Chancellor's message — a portrait card with the name, designation and message, beside stacked vision / mission cards",
      componentFields: ABOUT_1057_CONSTANTS.ABOUT_FIELDS_CONFIG,
      ...extractMetadataFields(ABOUT_1057_CONSTANTS),
      showTable: true,
      setAction: setAbout1057,
      formComponent: Form_1,
      previewComponent: About1057,
      selector: (state: PageState) => state.about_1057,
      createMethod: createAbout,
      updateMethod: updateAbout,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget drops status-false rows itself,
              // so the preview must see the same rows the live page does.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: ABOUT_VARIANTS.ABOUT_1028,
      name: "About 1028 — BDU About Us",
      description: "BDU About Us — square image, gradient rule and Know More link",
      componentFields: ABOUT_1028_CONSTANTS.ABOUT_FIELDS_CONFIG,
      ...extractMetadataFields(ABOUT_1028_CONSTANTS),
      showTable: true,
      setAction: setAbout1028,
      formComponent: Form_1,
      previewComponent: About1028,
      selector: (state: PageState) => state.about_1028,
      createMethod: createAbout,
      updateMethod: updateAbout,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.KEY_NUMBERS]: [
    {
      id: KEY_NUMBERS_VARIANTS.KEY_NUMBERS_1,
      name: "Key Numbers 1",
      description: "Key statistics and numbers",
      componentFields: KEY_NUMBERS_1_CONSTANTS.KEY_NUMBERS_FIELDS_CONFIG,
      ...extractMetadataFields(KEY_NUMBERS_1_CONSTANTS),
      showTable: true,
      setAction: setKeyNumbers1,
      formComponent: Form_1,
      previewComponent: Stats,
      selector: (state: PageState) => state.key_numbers_1,
      createMethod: createKeyNumbers,
      updateMethod: updateKeyNumbers,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              enable_animation: Boolean(
                data.enable_animation === true || data.enable_animation === 1,
              ),
              status: true,
            },
          },
        };
      },
    },
    {
      id: KEY_NUMBERS_VARIANTS.KEY_NUMBERS_1036,
      name: "Key Numbers 1036 — BDU Programme key features",
      description:
        "BDU programme key features — circled icon and one line of copy on a solid bar",
      componentFields: KEY_NUMBERS_1036_CONSTANTS.KEY_NUMBERS_FIELDS_CONFIG,
      ...extractMetadataFields(KEY_NUMBERS_1036_CONSTANTS),
      showTable: true,
      setAction: setKeyNumbers1036,
      formComponent: Form_1,
      previewComponent: KeyNumbers1036,
      selector: (state: PageState) => state.key_numbers_1036,
      createMethod: createKeyNumbers,
      updateMethod: updateKeyNumbers,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: KEY_NUMBERS_VARIANTS.KEY_NUMBERS_1039,
      name: "Key Numbers 1039 — BDU Programme key content",
      description:
        "BDU programme key content — big number and label on a solid bar with hairline separators",
      componentFields: KEY_NUMBERS_1039_CONSTANTS.KEY_NUMBERS_FIELDS_CONFIG,
      ...extractMetadataFields(KEY_NUMBERS_1039_CONSTANTS),
      showTable: true,
      setAction: setKeyNumbers1039,
      formComponent: Form_1,
      previewComponent: KeyNumbers1039,
      selector: (state: PageState) => state.key_numbers_1039,
      createMethod: createKeyNumbers,
      updateMethod: updateKeyNumbers,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: KEY_NUMBERS_VARIANTS.KEY_NUMBERS_1026,
      name: "Key Numbers 1026 — BDU Stats",
      description: "BDU Stats — circled icon, count and label on a solid bar",
      componentFields: KEY_NUMBERS_1026_CONSTANTS.KEY_NUMBERS_FIELDS_CONFIG,
      ...extractMetadataFields(KEY_NUMBERS_1026_CONSTANTS),
      showTable: true,
      setAction: setKeyNumbers1026,
      formComponent: Form_1,
      previewComponent: KeyNumbers1026,
      selector: (state: PageState) => state.key_numbers_1026,
      createMethod: createKeyNumbers,
      updateMethod: updateKeyNumbers,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: KEY_NUMBERS_VARIANTS.KEY_NUMBERS_2,
      name: "Key Numbers 2",
      description: "Key numbers with pre-title, title, description and gradient styling",
      componentFields: KEY_NUMBERS_2_CONSTANTS.KEY_NUMBERS_FIELDS_CONFIG,
      ...extractMetadataFields(KEY_NUMBERS_2_CONSTANTS),
      showTable: true,
      setAction: setKeyNumbers2,
      formComponent: Form_1,
      previewComponent: KeyNumbers2,
      selector: (state: PageState) => state.key_numbers_2,
      createMethod: createKeyNumbers,
      updateMethod: updateKeyNumbers,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
              enable_animation: data.enable_animation || false,
            },
          },
        };
      },
    },
    {
      id: KEY_NUMBERS_VARIANTS.KEY_NUMBERS_5006,
      name: "Key Numbers 5006 — KUK Key Features",
      description:
        "KUK programme key features — an icon over one line of copy, as a 2x2 grid below 768px and a single row above it",
      componentFields: KEY_NUMBERS_5006_CONSTANTS.KEY_NUMBERS_FIELDS_CONFIG,
      ...extractMetadataFields(KEY_NUMBERS_5006_CONSTANTS),
      showTable: true,
      setAction: setKeyNumbers5006,
      formComponent: Form_1,
      previewComponent: KeyNumbers5006,
      selector: (state: PageState) => state.key_numbers_5006,
      createMethod: createKeyNumbers,
      updateMethod: updateKeyNumbers,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
            // Not activateMetadata: the widget drops status-false rows
            // itself, so the preview must see the same rows the live page does.
            metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: isCmsShowFlagOn(data.status, true),
            },
          },
        };
      },
    },
    {
      id: KEY_NUMBERS_VARIANTS.KEY_NUMBERS_1055,
      name: "Key Numbers 1055 — KUK Home Stats",
      description:
        "KUK home stats — an icon over a thousands-separated count and its label, on a gradient bar with hairline separators",
      componentFields: KEY_NUMBERS_1055_CONSTANTS.KEY_NUMBERS_FIELDS_CONFIG,
      ...extractMetadataFields(KEY_NUMBERS_1055_CONSTANTS),
      showTable: true,
      setAction: setKeyNumbers1055,
      formComponent: Form_1,
      previewComponent: KeyNumbers1055,
      selector: (state: PageState) => state.key_numbers_1055,
      createMethod: createKeyNumbers,
      updateMethod: updateKeyNumbers,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget drops status-false rows itself,
              // so the preview must see the same rows the live page does.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: KEY_NUMBERS_VARIANTS.KEY_NUMBERS_5007,
      name: "Key Numbers 5007 — KUK Learning Highlights",
      description:
        "KUK learning highlights — a large figure over its label on a gradient band",
      componentFields: KEY_NUMBERS_5007_CONSTANTS.KEY_NUMBERS_FIELDS_CONFIG,
      ...extractMetadataFields(KEY_NUMBERS_5007_CONSTANTS),
      showTable: true,
      setAction: setKeyNumbers5007,
      formComponent: Form_1,
      previewComponent: KeyNumbers5007,
      selector: (state: PageState) => state.key_numbers_5007,
      createMethod: createKeyNumbers,
      updateMethod: updateKeyNumbers,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
            // Not activateMetadata: the widget drops status-false rows
            // itself, so the preview must see the same rows the live page does.
            metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: isCmsShowFlagOn(data.status, true),
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.DIGITAL_LEARNING]: [
    {
      id: DIGITAL_LEARNING_VARIANTS.DIGITAL_LEARNING_1,
      name: "Digital Learning 1",
      description:
        "Digital learning with pre-title, title, description, CTA and metadata",
      componentFields: DIGITAL_LEARNING_1_CONSTANTS.KEY_NUMBERS_FIELDS_CONFIG,
      ...extractMetadataFields(DIGITAL_LEARNING_1_CONSTANTS),
      showTable: true,
      setAction: setDigitalLearning1,
      formComponent: Form_1,
      previewComponent: DigitalLearning1,
      selector: (state: PageState) => state.digital_learning_1,
      createMethod: createDigitalLearning,
      updateMethod: updateDigitalLearning,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.ACCREDITATION]: [
    {
      id: ACCREDITATION_VARIANTS.ACCREDITATION_1,
      name: "Accreditation 1",
      description: "Accreditations and certifications",
      componentFields: ACCREDITATION_1_CONSTANTS.ACCREDITATION_FIELDS_CONFIG,
      ...extractMetadataFields(ACCREDITATION_1_CONSTANTS),
      showTable: true,
      setAction: setAccreditation1,
      formComponent: Form_1,
      previewComponent: Accreditation,
      selector: (state: PageState) => state.accreditation_1,
      createMethod: createAccreditation,
      updateMethod: updateAccreditation,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: ACCREDITATION_VARIANTS.ACCREDITATION_5,
      name: "Accreditation 5 — Recognition cards",
      description:
        "Accreditation 5 — Recognition cards",
      componentFields: ACCREDITATION_5_CONSTANTS.ACCREDITATION_5_FIELDS_CONFIG,
      ...extractMetadataFields(ACCREDITATION_5_CONSTANTS),
      showTable: true,
      setAction: setAccreditation5,
      formComponent: Form_1,
      previewComponent: Accreditation5,
      selector: (state: PageState) => state.accreditation_5,
      createMethod: createAccreditation,
      updateMethod: updateAccreditation,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: ACCREDITATION_VARIANTS.ACCREDITATION_1052,
      name: "Accreditation 1052 — KUK Home strip",
      description:
        "KUK home accreditation strip — each badge over its label on a tinted band, with a hairline rule between neighbours",
      componentFields: ACCREDITATION_1052_CONSTANTS.ACCREDITATION_FIELDS_CONFIG,
      ...extractMetadataFields(ACCREDITATION_1052_CONSTANTS),
      showTable: true,
      setAction: setAccreditation1052,
      formComponent: Form_1,
      previewComponent: Accreditation1052,
      selector: (state: PageState) => state.accreditation_1052,
      createMethod: createAccreditation,
      updateMethod: updateAccreditation,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget drops status-false rows itself,
              // so the preview must see the same rows the live page does.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: ACCREDITATION_VARIANTS.ACCREDITATION_1000,
      name: "Accreditation 1000 — Global recognition carousel",
      description:
        "Accreditation 1000 — carousel of accreditation cards (logo, title, description) over a full-bleed background image; migrated from the Chandigarh LP.",
      componentFields: ACCREDITATION_1000_CONSTANTS.ACCREDITATION_1000_FIELDS_CONFIG,
      ...extractMetadataFields(ACCREDITATION_1000_CONSTANTS),
      showTable: true,
      setAction: setAccreditation1000,
      formComponent: Form_1,
      previewComponent: Accreditation1000,
      selector: (state: PageState) => state.accreditation_1000,
      createMethod: createAccreditation,
      updateMethod: updateAccreditation,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: ACCREDITATION_VARIANTS.ACCREDITATION_3,
      name: "Accreditation 3 — Vision / Mission / Values",
      description:
        "Accreditation 3 — Vision / Mission / Values",
      componentFields: ACCREDITATION_3_CONSTANTS.ACCREDITATION_3_FIELDS_CONFIG,
      ...extractMetadataFields(ACCREDITATION_3_CONSTANTS),
      showTable: true,
      setAction: setAccreditation3,
      formComponent: Form_1,
      previewComponent: Accreditation3,
      selector: (state: PageState) => state.accreditation_3,
      createMethod: createAccreditation,
      updateMethod: updateAccreditation,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: ACCREDITATION_VARIANTS.ACCREDITATION_4,
      name: "Accreditation 4 — Selectable highlights",
      description:
        "Accreditation 4 — left column of selectable cards (icon + title); selecting a card shows its title and description on the right.",
      componentFields: ACCREDITATION_4_CONSTANTS.ACCREDITATION_4_FIELDS_CONFIG,
      ...extractMetadataFields(ACCREDITATION_4_CONSTANTS),
      showTable: true,
      setAction: setAccreditation4,
      formComponent: Form_1,
      previewComponent: Accreditation4,
      selector: (state: PageState) => state.accreditation_4,
      createMethod: createAccreditation,
      updateMethod: updateAccreditation,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: ACCREDITATION_VARIANTS.ACCREDITATION_2,
      name: "Accreditation 2",
      description: "Ranking & Recognitions logo strip + carousel",
      componentFields: ACCREDITATION_2_CONSTANTS.ACCREDITATION_FIELDS_CONFIG,
      ...extractMetadataFields(ACCREDITATION_2_CONSTANTS),
      showTable: true,
      setAction: setAccreditation2,
      formComponent: Form_1,
      previewComponent: Accreditation2,
      selector: (state: PageState) => state.accreditation_2,
      createMethod: createAccreditation,
      updateMethod: updateAccreditation,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.ACADEMIC_DELIVERABLES]: [
    {
      id: ACADEMIC_DELIVERABLES_VARIANTS.ACADEMIC_DELIVERABLES_1,
      name: "Academic Deliverables 1",
      description: "Academic programs and deliverables",
      componentFields:
        ACADEMIC_DELIVERABLES_1_CONSTANTS.ACADEMIC_DELIVERABLES_FIELDS_CONFIG,
      ...extractMetadataFields(ACADEMIC_DELIVERABLES_1_CONSTANTS),
      showTable: false,
      setAction: setAcademicDeliverables1,
      formComponent: Form_1,
      previewComponent: AcademicDeliverables,
      selector: (state: PageState) => state.academic_deliverables_1,
      createMethod: createDeliverable,
      updateMethod: updateDeliverable,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: ACADEMIC_DELIVERABLES_VARIANTS.ACADEMIC_DELIVERABLES_1031,
      name: "Academic Deliverables 1031 — BDU Hands-On Learning",
      description: "BDU Hands-On Learning — tinted background art and numbered icon rows",
      componentFields: ACADEMIC_DELIVERABLES_1031_CONSTANTS.ACADEMIC_DELIVERABLES_FIELDS_CONFIG,
      ...extractMetadataFields(ACADEMIC_DELIVERABLES_1031_CONSTANTS),
      showTable: true,
      setAction: setAcademicDeliverables1031,
      formComponent: Form_1,
      previewComponent: AcademicDeliverables1031,
      selector: (state: PageState) => state.academic_deliverables_1031,
      createMethod: createDeliverable,
      updateMethod: updateDeliverable,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: ACADEMIC_DELIVERABLES_VARIANTS.ACADEMIC_DELIVERABLES_2,
      name: "Academic Deliverables 2 — Placements",
      description:
        "Academic Deliverables 2 - Animated salary-package scroll on a primary background with an 'Alumni Work At' recruiter image strip",
      componentFields:
        ACADEMIC_DELIVERABLES_2_CONSTANTS.ACADEMIC_DELIVERABLES_FIELDS_CONFIG,
      ...extractMetadataFields(ACADEMIC_DELIVERABLES_2_CONSTANTS),
      showTable: true,
      setAction: setAcademicDeliverables2,
      formComponent: Form_1,
      previewComponent: AcademicDeliverables2,
      selector: (state: PageState) => state.academic_deliverables_2,
      createMethod: createDeliverable,
      updateMethod: updateDeliverable,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const meta =
          data.metadata?.length > 0
            ? activateMetadata(data.metadata)
            : ACADEMIC_DELIVERABLES_2_CONSTANTS.SEED_METADATA;
        return {
          data: {
            data: {
              ...data,
              section_background_color:
                data.section_background_color ?? "#db5803",
              recruiters_title: data.recruiters_title ?? "Alumni {Work At}",
              metadata: meta,
              status: true,
            },
          },
        };
      },
    },
    {
      id: ACADEMIC_DELIVERABLES_VARIANTS.ACADEMIC_DELIVERABLES_1001,
      name: "Academic Deliverables 1001 — Placement Success",
      description:
        "Academic Deliverables 1001 — Static salary-stats band over a soft background, followed by a white hiring-partners band with heading, sub-title, lead CTA and partner-logo strip; migrated from the Chandigarh LP.",
      componentFields:
        ACADEMIC_DELIVERABLES_1001_CONSTANTS.ACADEMIC_DELIVERABLES_1001_FIELDS_CONFIG,
      ...extractMetadataFields(ACADEMIC_DELIVERABLES_1001_CONSTANTS),
      showTable: true,
      setAction: setAcademicDeliverables1001,
      formComponent: Form_1,
      previewComponent: AcademicDeliverables1001,
      selector: (state: PageState) => state.academic_deliverables_1001,
      createMethod: createDeliverable,
      updateMethod: updateDeliverable,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const meta =
          data.metadata?.length > 0
            ? activateMetadata(data.metadata)
            : ACADEMIC_DELIVERABLES_1001_CONSTANTS.SEED_METADATA;
        return {
          data: {
            data: {
              ...data,
              section_background_color:
                data.section_background_color ?? "#FFDBD9",
              recruiters_title:
                data.recruiters_title ?? "Our Hiring {Partners}",
              button_text: data.button_text ?? "Get Started",
              metadata: meta,
              status: true,
            },
          },
        };
      },
    },
    {
      id: ACADEMIC_DELIVERABLES_VARIANTS.ACADEMIC_DELIVERABLES_1002,
      name: "Academic Deliverables 1002 — Placement Success (IIT KGP)",
      description:
        "Academic Deliverables 1002 - Static salary-stats band, followed by a white hiring-partners band with heading, sub-title, lead CTA and partner-logo strip; migrated from the IIT KGP LP.",
      componentFields:
        ACADEMIC_DELIVERABLES_1002_CONSTANTS.ACADEMIC_DELIVERABLES_1002_FIELDS_CONFIG,
      ...extractMetadataFields(ACADEMIC_DELIVERABLES_1002_CONSTANTS),
      showTable: true,
      setAction: setAcademicDeliverables1002,
      formComponent: Form_1,
      previewComponent: AcademicDeliverables1002,
      selector: (state: PageState) => state.academic_deliverables_1002,
      createMethod: createDeliverable,
      updateMethod: updateDeliverable,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const meta =
          data.metadata?.length > 0
            ? activateMetadata(data.metadata)
            : ACADEMIC_DELIVERABLES_1002_CONSTANTS.SEED_METADATA;
        return {
          data: {
            data: {
              ...data,
              recruiters_title:
                data.recruiters_title ?? "Our learners work at {top companies}",
              button_text: data.button_text ?? "Get Started",
              metadata: meta,
              status: true,
            },
          },
        };
      },
    },
    {
      id: ACADEMIC_DELIVERABLES_VARIANTS.ACADEMIC_DELIVERABLES_1006,
      name: "Academic Deliverables 1006 — Industry Insights (CU)",
      description:
        "Academic Deliverables 1006 - Centered title and lead CTA over a full-bleed background image, followed by a carousel of industry-insight cards (source logo, highlighted description, stat illustration); migrated from the Chandigarh LP.",
      componentFields:
        ACADEMIC_DELIVERABLES_1006_CONSTANTS.ACADEMIC_DELIVERABLES_1006_FIELDS_CONFIG,
      ...extractMetadataFields(ACADEMIC_DELIVERABLES_1006_CONSTANTS),
      showTable: true,
      setAction: setAcademicDeliverables1006,
      formComponent: Form_1,
      previewComponent: AcademicDeliverables1006,
      selector: (state: PageState) => state.academic_deliverables_1006,
      createMethod: createDeliverable,
      updateMethod: updateDeliverable,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const meta =
          data.metadata?.length > 0
            ? activateMetadata(data.metadata)
            : ACADEMIC_DELIVERABLES_1006_CONSTANTS.SEED_METADATA;
        return {
          data: {
            data: {
              ...data,
              title: data.title ?? "Industry {Insights}",
              button_text: data.button_text ?? "Get Started",
              metadata: meta,
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.SCHOLAR]: [
    {
      id: SCHOLAR_VARIANTS.SCHOLAR_1,
      name: "Scholar 1",
      description: "Scholarship information",
      componentFields: SCHOLAR_1_CONSTANTS.SCHOLAR_FIELDS_CONFIG,
      ...extractMetadataFields(SCHOLAR_1_CONSTANTS),
      showTable: true,
      setAction: setScholar1,
      formComponent: Form_1,
      previewComponent: Scholar,
      selector: (state: PageState) => state.scholar_1,
      createMethod: createScholar,
      updateMethod: updateScholar,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.LATEST_BLOGS]: [
    {
      id: LATEST_BLOGS_VARIANTS.LATEST_BLOGS_1,
      name: "Latest Blogs 1",
      description:
        "Latest blog cards with date, title, read more, and carousel (Atlas)",
      componentFields: LATEST_BLOGS_1_CONSTANTS.LATEST_BLOGS_FIELDS_CONFIG,
      ...extractMetadataFields(LATEST_BLOGS_1_CONSTANTS),
      showTable: false,
      setAction: setLatestBlogs1,
      formComponent: Form_1,
      previewComponent: LatestBlogs1,
      selector: (state: PageState) => state.latest_blogs_1,
      createMethod: createLatestBlog,
      updateMethod: updateLatestBlog,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: LATEST_BLOGS_VARIANTS.LATEST_BLOGS_2,
      name: "Latest Blogs 2",
      description:
        "Highlighted main title + Popular Blogs arrow carousel (is_popular blogs, university-branded cards)",
      componentFields: LATEST_BLOGS_2_CONSTANTS.LATEST_BLOGS_FIELDS_CONFIG,
      ...extractMetadataFields(LATEST_BLOGS_2_CONSTANTS),
      showTable: false,
      setAction: setLatestBlogs2,
      formComponent: Form_1,
      previewComponent: LatestBlogs2,
      selector: (state: PageState) => state.latest_blogs_2,
      createMethod: createLatestBlog,
      updateMethod: updateLatestBlog,
      // Variant 2 is API-driven: never send a metadata snapshot or the
      // fetch toggle — the widget reads popular blogs live from the filter API.
      transformPayload: ({ metadata, fetch_latest_blogs, ...rest }: any) => rest,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
              university_id: 1,
              popular_blogs: {
                items: [
                  {
                    id: 1,
                    path: "/blog/sample-popular-blog",
                    blog_banner_image: "",
                    blog_title: "Sample popular blog title for preview",
                    blog_category_ids: [1],
                    is_popular: true,
                    created_at: "2026-02-12T12:00:00.000Z",
                  },
                ],
                total: 1,
                offset: 0,
                limit: 12,
              },
              new_blogs: {
                items: [
                  {
                    id: 2,
                    path: "/blog/sample-new-blog",
                    blog_banner_image: "",
                    blog_title: "Sample new blog title for preview",
                    blog_category_ids: [1],
                    created_at: "2026-02-20T12:00:00.000Z",
                  },
                ],
                total: 1,
                offset: 0,
                limit: 12,
              },
              categories: [
                {
                  id: 1,
                  name: "Sample Category",
                  background_image_url: "",
                  icon_url: "",
                  is_default_category: false,
                },
              ],
              featured_blogs: {
                items: [
                  {
                    id: 3,
                    path: "/blog/sample-featured-blog",
                    blog_banner_image: "",
                    blog_title: "Sample featured blog title for preview",
                    blog_category_ids: [1],
                    is_featured: true,
                    created_at: "2026-02-22T12:00:00.000Z",
                  },
                ],
                total: 1,
                offset: 0,
                limit: 12,
              },
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.POPULAR_BLOGS]: [
    {
      id: POPULAR_BLOGS_VARIANTS.POPULAR_BLOGS_1,
      name: "Popular Blogs 1",
      description:
        "Featured post (left) + list (right); manual posts or auto-fill from published blogs",
      componentFields:
        POPULAR_BLOGS_1_CONSTANTS.POPULAR_BLOGS_FIELDS_CONFIG,
      ...extractMetadataFields(POPULAR_BLOGS_1_CONSTANTS),
      showTable: true,
      formComponent: Form_1,
      previewComponent: PopularBlogs1,
      selector: (state: PageState) => state.popular_blogs_1,
      createMethod: createPopularBlog,
      updateMethod: updatePopularBlog,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.AUTHOR_PROFILE]: [
    {
      id: AUTHOR_PROFILE_VARIANTS.AUTHOR_PROFILE_1,
      name: "Author Profile 1",
      description:
        "Author card, About column and the author's Published / Most Popular blogs (Atlas)",
      componentFields: AUTHOR_PROFILE_1_CONSTANTS.AUTHOR_PROFILE_FIELDS_CONFIG,
      ...extractMetadataFields(AUTHOR_PROFILE_1_CONSTANTS),
      showTable: false,
      setAction: setAuthorProfile3000,
      formComponent: Form_1,
      previewComponent: AuthorProfile1,
      selector: (state: PageState) => state.author_profile_3000,
      createMethod: createAuthorProfile,
      updateMethod: updateAuthorProfile,
      // The author dropdown lists published authors; details are joined server-side.
      dynamicOptionsConfig: {
        authors: {
          fetch: getActiveAuthorsForWidget,
          transform: (author: any) => ({
            value: String(author.id),
            label: author.name,
            author,
          }),
        },
      },
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        // The saved payload only holds `author_id`. The widget resolves the author
        // itself (GET /author/public/:id) when `author` is absent, which covers the
        // editor preview — only published authors are selectable, so it always hits.
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
              author: data.author ?? null,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.AUTHOR_CARD]: [
    {
      id: AUTHOR_CARD_VARIANTS.AUTHOR_CARD_1,
      name: "Author Card 1",
      description:
        "ONLY RENDERS ON A BLOG PAGE THAT HAS AN AUTHOR LINKED — compact bio card (photo, name, article count, bio) read from the linked author",
      componentFields: AUTHOR_CARD_1_CONSTANTS.AUTHOR_CARD_FIELDS_CONFIG,
      showTable: false,
      setAction: setAuthorCard3000,
      formComponent: Form_1,
      previewComponent: AuthorCard1,
      selector: (state: PageState) => state.author_card_3000,
      createMethod: createAuthorCard,
      updateMethod: updateAuthorCard,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        // No page behind the widget preview, so the widget falls back to its
        // stand-in author (isCmsPreview) purely to show the chosen colours.
        return { data: { data: { ...data, status: true } } };
      },
    },
  ],
  [SECTION_TYPES.AUTHOR_BYLINE]: [
    {
      id: AUTHOR_BYLINE_VARIANTS.AUTHOR_BYLINE_1,
      name: "Author Byline 1",
      description:
        "ONLY RENDERS ON A BLOG PAGE THAT HAS AN AUTHOR LINKED — \"By <author>\" with the page's updated date, read time and views, plus share links",
      componentFields: AUTHOR_BYLINE_1_CONSTANTS.AUTHOR_BYLINE_FIELDS_CONFIG,
      showTable: false,
      setAction: setAuthorByline3000,
      formComponent: Form_1,
      previewComponent: AuthorByline1,
      selector: (state: PageState) => state.author_byline_3000,
      createMethod: createAuthorByline,
      updateMethod: updateAuthorByline,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        // Sample meta so the editor sees the full line; live pages read the real
        // updated_at / read_time / views off the page row.
        return {
          data: { data: { ...data, status: true } },
          pageMeta: { updated_at: new Date().toISOString(), read_time: 10, views: 951840 },
        };
      },
    },
  ],
  [SECTION_TYPES.BLOG_LIST]: [
    {
      id: BLOG_LIST_VARIANTS.BLOG_LIST_1,
      name: "Blog list 1",
      description:
        "Category filters, blog cards from published pages, load more (Atlas)",
      componentFields: BLOG_LIST_1_CONSTANTS.BLOG_LIST_FIELDS_CONFIG,
      showTable: false,
      setAction: setBlogList1,
      formComponent: Form_1,
      previewComponent: BlogList1,
      selector: (state: PageState) => state.blog_list_1,
      createMethod: createBlogList,
      updateMethod: updateBlogList,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: true,
              university_id: 1,
              categories: [
                {
                  id: 1,
                  name: "Sample category",
                  icon_url: "",
                  background_image_url: "",
                },
              ],
              blogs: {
                items: [
                  {
                    id: 1,
                    path: "/blog/sample",
                    blog_banner_image: "",
                    blog_title: "Preview blog title",
                    blog_description: "Preview description for the blog card.",
                    blog_category_ids: [1],
                    created_at: "2024-09-12T12:00:00.000Z",
                  },
                ],
                total: 1,
                offset: 0,
                limit: 6,
              },
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.COUNSELOR_CALL_CTA]: [
    {
      id: COUNSELOR_CALL_CTA_VARIANTS.COUNSELOR_CALL_CTA_1,
      name: "Counselor call CTA 1",
      description:
        "Academic counselor pill — phone field + call button (Atlas)",
      componentFields:
        COUNSELOR_CALL_CTA_1_CONSTANTS.COUNSELOR_CALL_CTA_FIELDS_CONFIG,
      ...extractMetadataFields(COUNSELOR_CALL_CTA_1_CONSTANTS),
      showTable: false,
      setAction: setCounselorCallCta1,
      formComponent: Form_1,
      previewComponent: CounselorCallCta1,
      selector: (state: PageState) => state.counselor_call_cta_1,
      createMethod: createCounselorCallCta,
      updateMethod: updateCounselorCallCta,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.BREADCRUMB_BANNER]: [
    {
      id: BREADCRUMB_BANNER_VARIANTS.BREADCRUMB_BANNER_1,
      name: "Breadcrumb 1",
      description:
        "Manual breadcrumb strip — scrolls horizontally on small screens; sits in the blog article column on blog pages",
      componentFields:
        BREADCRUMB_BANNER_1_CONSTANTS.BREADCRUMB_BANNER_FIELDS_CONFIG,
      ...extractMetadataFields(BREADCRUMB_BANNER_1_CONSTANTS),
      showTable: false,
      setAction: setBreadcrumbBanner1,
      formComponent: Form_1,
      previewComponent: BreadcrumbBanner1,
      selector: (state: PageState) => state.breadcrumb_banner_1,
      createMethod: createBreadcrumbBanner,
      updateMethod: updateBreadcrumbBanner,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.COUNSELING]: [
    {
      id: COUNSELING_VARIANTS.COUNSELING_1,
      name: "Counseling 1",
      description: "Counseling section with CTA button",
      componentFields: COUNSELING_1_CONSTANTS.COUNSELING_FIELDS_CONFIG,
      ...extractMetadataFields(COUNSELING_1_CONSTANTS),
      showTable: false,
      setAction: setCounseling1,
      formComponent: Form_1,
      previewComponent: GetFreeCounselling1,
      selector: (state: PageState) => state.counseling_1,
      createMethod: createCounseling,
      updateMethod: updateCounseling,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              title: data.title || "",
              title_text_color: data.title_text_color || "#000000",
              sub_title: data.sub_title || "",
              sub_title_text_color: data.sub_title_text_color || "#000000",
              cta_text: data.cta_text || "Get Started",
              cta_text_color: data.cta_text_color || "#FFFFFF",
              cta_button_background_color:
                data.cta_button_background_color || "#000000",
              cta_button_icon: data.cta_button_icon || "",
              cta_button_icon_alt: data.cta_button_icon_alt || "Get Started",
              background_color: data.background_color || "#FFFFFF",
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.PROGRAM_PROSPECTS]: [
    {
      id: PROGRAM_PROSPECTS_VARIANTS.PROGRAM_PROSPECTS_1,
      name: "Program Prospects 1",
      description: "Program prospects with career paths",
      componentFields:
        PROGRAM_PROSPECTS_1_CONSTANTS.PROGRAM_PROSPECTS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_PROSPECTS_1_CONSTANTS),
      showTable: true,
      setAction: setProgramProspects1,
      formComponent: Form_1,
      previewComponent: ProgramProspects1,
      selector: (state: PageState) => state.program_prospects_1,
      createMethod: createProgramProspects,
      updateMethod: updateProgramProspects,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.COURSE_SPECIALISATIONS]: [
    {
      id: COURSE_SPECIALISATIONS_VARIANTS.COURSE_SPECIALISATIONS_1,
      name: "Course Specialisations 1",
      description: "Course specialisations with desktop and mobile variants",
      componentFields:
        COURSE_SPECIALISATIONS_1_CONSTANTS.COURSE_SPECIALISATIONS_FIELDS_CONFIG,
      ...extractMetadataFields(COURSE_SPECIALISATIONS_1_CONSTANTS),
      showTable: true,
      setAction: setCourseSpecialisations1,
      formComponent: Form_1,
      previewComponent: CourseSpecialisation1,
      selector: (state: PageState) => state.course_specialisations_1,
      createMethod: createCourseSpecialisations,
      updateMethod: updateCourseSpecialisations,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.ACCORDION]: [
    {
      id: ACCORDION_VARIANTS.ACCORDION_1,
      name: "Accordion 1",
      description: "Accordion section with expandable items",
      componentFields: ACCORDION_1_CONSTANTS.ACCORDION_FIELDS_CONFIG,
      ...extractMetadataFields(ACCORDION_1_CONSTANTS),
      showTable: true,
      setAction: setAccordion1,
      formComponent: Form_1,
      previewComponent: ExaminationGuidelines1,
      selector: (state: PageState) => state.accordion_1,
      createMethod: createAccordion,
      updateMethod: updateAccordion,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.LIVE_LECTURE]: [
    {
      id: LIVE_LECTURE_VARIANTS.LIVE_LECTURE_1,
      name: "Live Lecture 1",
      description: "Live lecture widget with calendar view",
      componentFields: LIVE_LECTURE_1_CONSTANTS.LIVE_LECTURE_FIELDS_CONFIG,
      defaultRow: LIVE_LECTURE_1_CONSTANTS.DEFAULT_ROW,
      showTable: false,
      setAction: setLiveLecture1,
      formComponent: Form_1,
      previewComponent: LiveLecture1,
      selector: (state: PageState) => state.live_lecture_1,
      createMethod: createLiveLectureConfig,
      updateMethod: updateLiveLectureConfig,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: true,
            },
          },
        };
      },
    },
    {
      id: LIVE_LECTURE_VARIANTS.LIVE_LECTURE_1050,
      name: "Live Lecture 1050 — BDU-style calendar",
      description:
        "Live Lecture 1050 — the BDU live-session lecture schedule: an HTML content block, a course/semester/batch filter row with a red-gradient Submit button, a bordered month/day calendar with a hover and touch tooltip, and a tentative-schedule footnote. Same fields as Live Lecture 1011, defaulted to the BDU palette; the active view button is filled rather than underlined, so there is no Active Bar Color.",
      componentFields: LIVE_LECTURE_1050_CONSTANTS.LIVE_LECTURE_FIELDS_CONFIG,
      defaultRow: LIVE_LECTURE_1050_CONSTANTS.DEFAULT_ROW,
      showTable: false,
      setAction: setLiveLecture1050,
      formComponent: Form_1,
      previewComponent: LiveLecture1050,
      selector: (state: PageState) => state.live_lecture_1050,
      createMethod: createLiveLectureConfig,
      updateMethod: updateLiveLectureConfig,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: true,
            },
          },
        };
      },
    },
    {
      id: LIVE_LECTURE_VARIANTS.LIVE_LECTURE_1011,
      name: "Live Lecture 1011 — Chandigarh-style calendar",
      description:
        "Live Lecture 1011 — the CU campus-portal lecture schedule: an HTML content block, a course/semester/batch filter row with a Submit button, a bordered month/day calendar with a hover and touch tooltip, and a tentative-schedule footnote. Same fields as Live Lecture 1, defaulted to the CU palette.",
      componentFields: LIVE_LECTURE_1011_CONSTANTS.LIVE_LECTURE_FIELDS_CONFIG,
      defaultRow: LIVE_LECTURE_1011_CONSTANTS.DEFAULT_ROW,
      showTable: false,
      setAction: setLiveLecture1011,
      formComponent: Form_1,
      previewComponent: LiveLecture1011,
      selector: (state: PageState) => state.live_lecture_1011,
      createMethod: createLiveLectureConfig,
      updateMethod: updateLiveLectureConfig,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: true,
            },
          },
        };
      },
    },
  ],

  [SECTION_TYPES.SKILL_CARDS]: [
    {
      id: SKILL_CARDS_VARIANTS.SKILL_CARDS_1,
      name: "Skill Cards",
      description:
        "Heading + 2-per-row skill containers (image, title, subtitle, divider, paragraph), View More reveal, closing CTA",
      componentFields: SKILL_CARDS_3000_CONSTANTS.SKILL_CARDS_FIELDS_CONFIG,
      ...extractMetadataFields(SKILL_CARDS_3000_CONSTANTS),
      showTable: true,
      setAction: setSkillCards3000,
      formComponent: Form_1,
      previewComponent: SkillCards1,
      selector: (state: PageState) => state.skill_cards_3000,
      createMethod: createSkillCards,
      updateMethod: updateSkillCards,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
  ],
};
